import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Package, X, CheckSquare, Square, Download, Upload, Image, Video, ChevronLeft, ChevronRight, Rows, Grid3x3, LayoutGrid, Star, Sparkles, Loader2, Camera, FileText, AlertTriangle, CheckCircle, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { CurrencyInput } from '../components/CurrencyInput';
import AnimatedSearchBar from '../components/AnimatedSearchBar';
import { MultiImageUploader, VideoUploader } from './SiteEditor';
import { getProductSalesUnit } from '../lib/productPricing';
import { getColorHex } from '../lib/colors';
import VariacoesEditor from '../components/VariacoesEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('dados'); // 'dados' ou 'midia'
  
  // Seleção múltipla
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('products_view_mode') || 'linha');

  // AI Quick Register
  const [showAIModal, setShowAIModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [promoProduct, setPromoProduct] = useState(null); // {product, ativar, desconto_pct}
  const [aiPhoto, setAiPhoto] = useState(null);
  const [aiPhotoPreview, setAiPhotoPreview] = useState(null);
  const [aiHintNome, setAiHintNome] = useState('');
  const [aiHintCategoria, setAiHintCategoria] = useState('');
  const [aiHintDescricao, setAiHintDescricao] = useState('');
  const [aiPreco, setAiPreco] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiImageUrl, setAiImageUrl] = useState(null);

  const handleAIAnalyze = async () => {
    if (!aiPhoto) { toast.error('Envie uma foto do produto'); return; }
    setAiLoading(true);
    const fd = new FormData();
    fd.append('file', aiPhoto);
    if (aiHintNome) fd.append('hint_nome', aiHintNome);
    if (aiHintCategoria) fd.append('hint_categoria', aiHintCategoria);
    if (aiHintDescricao) fd.append('hint_descricao', aiHintDescricao);
    try {
      const resp = await axios.post(`${API}/upload/image-with-ai`, fd);
      const sugg = resp.data?.suggestions || {};
      // Garantir tipos esperados para evitar crash de render ("tela preta")
      const safe = {
        produto_sugerido: typeof sugg.produto_sugerido === 'string' ? sugg.produto_sugerido : (aiHintNome || ''),
        categoria_sugerida: typeof sugg.categoria_sugerida === 'string' ? sugg.categoria_sugerida : (aiHintCategoria || ''),
        descricao: typeof sugg.descricao === 'string' ? sugg.descricao : (aiHintDescricao || ''),
        especificacoes: typeof sugg.especificacoes === 'string' ? sugg.especificacoes : '',
        cores_detectadas: Array.isArray(sugg.cores_detectadas)
          ? sugg.cores_detectadas.filter(c => typeof c === 'string' && c.trim())
          : (typeof sugg.cores_detectadas === 'string' && sugg.cores_detectadas
              ? sugg.cores_detectadas.split(/[,;\s]+/).filter(Boolean)
              : []),
      };
      setAiSuggestions(safe);
      setAiImageUrl(resp.data?.image_url || null);
      if (sugg.erro_ia) {
        toast.warning('IA não respondeu — preencha manualmente os campos.');
      } else {
        toast.success('IA analisou a foto! Revise e confirme.');
      }
    } catch (err) {
      console.error('AI analyze error', err);
      toast.error(err.response?.data?.detail || 'Erro na análise IA');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAICreateProduct = async () => {
    if (!aiSuggestions || !aiImageUrl) return;
    const precoNum = parseFloat(String(aiPreco).replace(',', '.')) || 0;
    const produto = {
      produto: aiSuggestions.produto_sugerido || aiHintNome || 'Produto sem nome',
      categoria: aiSuggestions.categoria_sugerida || aiHintCategoria || '',
      descricao: aiSuggestions.descricao || aiHintDescricao || '',
      especificacoes: aiSuggestions.especificacoes || '',
      cores: aiSuggestions.cores_detectadas || [],
      fotos: [aiImageUrl],
      codigo: `AI-${Date.now().toString().slice(-6)}`,
      valor_caixa: precoNum,
      valor_por_caixa: precoNum,
      quantidade_estoque: 0,
      alerta_estoque: 50
    };
    try {
      await axios.post(`${API}/products`, produto);
      toast.success('Produto cadastrado com sucesso!');
      setShowAIModal(false);
      setAiPhoto(null);
      setAiPhotoPreview(null);
      setAiHintNome(''); setAiHintCategoria(''); setAiHintDescricao('');
      setAiPreco(''); setAiSuggestions(null); setAiImageUrl(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cadastrar produto');
    }
  }; // 'linha' | 'grade' | 'card'

  useEffect(() => {
    localStorage.setItem('products_view_mode', viewMode);
  }, [viewMode]);

  const categories = useMemo(() => {
    const cats = {};
    products.forEach(p => { const c = p.categoria || 'Outros'; cats[c] = (cats[c] || 0) + 1; });
    return Object.entries(cats).sort((a, b) => b[1] - a[1]);
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchSearch = !searchTerm || 
        p.produto?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        p.codigo?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCat = !filterCategory || p.categoria === filterCategory;
      return matchSearch && matchCat;
    });
  }, [products, searchTerm, filterCategory]);
  
  const [formData, setFormData] = useState({
    codigo: '',
    produto: '',
    valor_por_unidade: 0,
    valor_por_100: 0,
    valor_por_1000: 0,
    valor_por_caixa: 0,
    valor_pacote: 0,
    valor_por_kg: 0,
    quantidade_estoque: '',
    alerta_estoque: '50',
    // Novos campos para catálogo
    descricao: '',
    detalhes: '',
    categoria: '',
    tamanho: '',
    fotos: [],
    videos: [],
    cores: [],
    cores_foto_map: {},
    especificacoes: '',
    fotos_especificacoes: [],
    variacoes: []  // [{codigo, imagem, preco}]
  });

  const [detectandoCores, setDetectandoCores] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchSiteCategories();
  }, []);

  const [siteCategories, setSiteCategories] = useState([]); // categorias cadastradas em /api/categories

  const fetchSiteCategories = async () => {
    try {
      const resp = await axios.get(`${API}/categories`);
      setSiteCategories(Array.isArray(resp.data) ? resp.data : []);
    } catch (error) {
      console.error('Erro ao carregar categorias do site:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  // Detecta a cor dominante de cada nova foto enviada e vincula no mapa cores_foto_map.
  // Evita duplicar cores: se a mesma cor já existe, sufixa com "(2)", "(3)", etc.
  const detectarCoresPorFoto = async (novasFotosUrls) => {
    if (!Array.isArray(novasFotosUrls) || novasFotosUrls.length === 0) return;
    setDetectandoCores(true);
    try {
      // Snapshot do estado atual (evita race condition em uploads rápidos)
      const mapaAtual = { ...(formData.cores_foto_map || {}) };
      const coresAtuais = [...(formData.cores || [])];
      // Detecta sequencialmente para preservar a ordem dos uploads
      let novasDetectadas = 0;
      for (const fotoUrl of novasFotosUrls) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const resp = await axios.post(`${API}/products/detect-color`, { image_url: fotoUrl });
          const corDetectada = (resp.data?.name || '').trim();
          if (!corDetectada) continue;
          // Se a cor já existe no mapa, gera variante (Vermelho 2, Vermelho 3, ...)
          let nomeFinal = corDetectada;
          let idx = 2;
          while (Object.prototype.hasOwnProperty.call(mapaAtual, nomeFinal)) {
            nomeFinal = `${corDetectada} ${idx++}`;
          }
          mapaAtual[nomeFinal] = fotoUrl;
          if (!coresAtuais.includes(nomeFinal)) coresAtuais.push(nomeFinal);
          novasDetectadas++;
        } catch (e) {
          console.warn('Não foi possível detectar cor de', fotoUrl, e);
        }
      }
      setFormData(prev => ({
        ...prev,
        cores_foto_map: mapaAtual,
        cores: coresAtuais,
      }));
      if (novasDetectadas > 0) {
        toast.success(`${novasDetectadas} cor(es) detectada(s) automaticamente`);
      }
    } finally {
      setDetectandoCores(false);
    }
  };

  // Para produtos existentes: vincula as cores já listadas às fotos já enviadas
  // analisando a cor dominante de cada foto e procurando a melhor correspondência.
  const autoLinkCoresExistentes = async () => {
    if (!editingProduct?.id) {
      toast.info('Salve o produto primeiro para usar a vinculação automática');
      return;
    }
    const fotos = formData.fotos || [];
    if (fotos.length === 0) {
      toast.error('Adicione fotos ao produto antes de vincular cores');
      return;
    }
    setDetectandoCores(true);
    try {
      const resp = await axios.post(`${API}/products/${editingProduct.id}/auto-link-colors`);
      const data = resp.data || {};
      const novoMapa = data.cores_foto_map || {};
      const cores = data.auto_filled_cores ? Object.keys(novoMapa) : (formData.cores || []);
      setFormData(prev => ({ ...prev, cores_foto_map: novoMapa, cores }));
      const linked = data.linked || 0;
      const unlinked = (data.unlinked || []).length;
      if (linked > 0) {
        toast.success(`✅ ${linked} cor(es) vinculada(s) à(s) foto(s) correspondente(s)${unlinked ? ` (${unlinked} sem foto compatível)` : ''}`);
      } else {
        toast.warning('Nenhuma vinculação possível. Verifique se as fotos têm cores nítidas.');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao vincular cores');
    } finally {
      setDetectandoCores(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Remove marcadores transientes de UI que não devem ir ao backend
      const cleanFormData = { ...formData };
      Object.keys(cleanFormData).forEach(k => { if (k.startsWith('__active_')) delete cleanFormData[k]; });

      const valorPorUnidade = parseFloat(cleanFormData.valor_por_unidade) || 0;
      const valorPorCaixa = parseFloat(cleanFormData.valor_por_caixa) || 0;
      const valorPor1000 = parseFloat(cleanFormData.valor_por_1000) || 0;
      const valorPacote = parseFloat(cleanFormData.valor_pacote) || 0;
      const valorPor100 = parseFloat(cleanFormData.valor_por_100) || 0;
      const valorPorKg = parseFloat(cleanFormData.valor_por_kg) || 0;

      const data = {
        ...cleanFormData,
        valor_por_unidade: valorPorUnidade,
        valor_por_100: valorPor100,
        valor_por_1000: valorPor1000,
        valor_por_caixa: valorPorCaixa,
        valor_pacote: valorPacote,
        valor_por_kg: valorPorKg,
        quantidade_estoque: parseInt(cleanFormData.quantidade_estoque) || 0,
        alerta_estoque: parseInt(cleanFormData.alerta_estoque) || 50,
        // Sincroniza campos legados com os novos para evitar bug do "não salvou"
        // (openEditModal caía no fallback legado quando valor_por_* era 0).
        valor_mil: valorPor1000,
        valor_caixa: valorPorCaixa,
        // Novos campos
        descricao: cleanFormData.descricao || '',
        detalhes: cleanFormData.detalhes || '',
        categoria: cleanFormData.categoria || '',
        tamanho: cleanFormData.tamanho || '',
        fotos: cleanFormData.fotos || [],
        videos: cleanFormData.videos || [],
        cores: cleanFormData.cores || [],
        cores_foto_map: cleanFormData.cores_foto_map || {},
        especificacoes: cleanFormData.especificacoes || '',
        fotos_especificacoes: cleanFormData.fotos_especificacoes || [],
        variacoes: Array.isArray(cleanFormData.variacoes) ? cleanFormData.variacoes : []
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast.success('Produto atualizado com sucesso!');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Produto criado com sucesso!');
      }
      fetchProducts();
      fetchSiteCategories(); // recarregar categorias caso uma nova tenha sido auto-criada
      closeModal();
    } catch (error) {
      toast.error('Erro ao salvar produto');
      console.error('Erro:', error);
    }
  };

  const openNewModal = () => {
    setEditingProduct(null);
    setActiveTab('dados');
    setFormData({
      codigo: '',
      produto: '',
      valor_por_unidade: 0,
      valor_por_100: 0,
      valor_por_1000: 0,
      valor_por_caixa: 0,
      valor_pacote: 0,
      valor_por_kg: 0,
      quantidade_estoque: '',
      alerta_estoque: '50',
      descricao: '',
      detalhes: '',
      categoria: '',
      tamanho: '',
      fotos: [],
      videos: [],
      cores: [],
      cores_foto_map: {},
      especificacoes: '',
      fotos_especificacoes: [],
      variacoes: []
    });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setActiveTab('dados');

    // ⚠️ Use `??` (nullish coalescing) em vez de `||`.
    // Com `||`, valor 0 (usuário desativou o tipo) caia no fallback legado
    // (`valor_mil`, `valor_caixa`) e o chip voltava ATIVO ao reabrir, causando
    // a impressão de que "não salvou".
    const valorUnidade = product.valor_por_unidade ?? (product.quantidade_caixa > 0 ? product.valor_caixa / product.quantidade_caixa : 0);
    const valor100 = product.valor_por_100 ?? (valorUnidade * 100);
    const valor1000 = product.valor_por_1000 ?? product.valor_mil ?? 0;
    const valorCaixa = product.valor_por_caixa ?? product.valor_caixa ?? 0;
    const estoque = product.quantidade_estoque ?? product.quantidade_disponivel ?? 0;
    const alerta = product.alerta_estoque ?? 50;

    setFormData({
      codigo: product.codigo || '',
      produto: product.produto || '',
      valor_por_unidade: valorUnidade,
      valor_por_100: valor100,
      valor_por_1000: valor1000,
      valor_por_caixa: valorCaixa,
      valor_pacote: product.valor_pacote ?? product.valor_por_pacote ?? 0,
      valor_por_kg: product.valor_por_kg ?? 0,
      quantidade_estoque: String(estoque),
      alerta_estoque: String(alerta),
      descricao: product.descricao || '',
      detalhes: product.detalhes || '',
      categoria: product.categoria || '',
      tamanho: product.tamanho || '',
      fotos: product.fotos || [],
      videos: product.videos || [],
      cores: product.cores || [],
      cores_foto_map: product.cores_foto_map || {},
      especificacoes: product.especificacoes || '',
      fotos_especificacoes: product.fotos_especificacoes || [],
      variacoes: Array.isArray(product.variacoes) ? product.variacoes : [],
      // marca ativo qualquer tipo com valor > 0 na abertura (persistência da UI)
      __active_valor_por_unidade: valorUnidade > 0,
      __active_valor_por_100: valor100 > 0,
      __active_valor_por_1000: valor1000 > 0,
      __active_valor_por_caixa: valorCaixa > 0,
      __active_valor_pacote: (product.valor_pacote ?? product.valor_por_pacote ?? 0) > 0,
      __active_valor_por_kg: (product.valor_por_kg ?? 0) > 0,
    });
    setShowModal(true);
  };

  // ============ PROMOÇÃO ============
  const openPromoModal = (product) => {
    setPromoProduct({
      product,
      ativar: !!product.promocao_ativa,
      desconto_pct: product.desconto_promocional || 0,
    });
  };

  const savePromo = async () => {
    if (!promoProduct) return;
    const { product, ativar, desconto_pct } = promoProduct;
    const pct = Math.max(0, Math.min(99, parseFloat(desconto_pct) || 0));
    const valorUnit = product.valor_por_unidade
      || (product.quantidade_caixa > 0 ? (product.valor_caixa || 0) / product.quantidade_caixa : 0)
      || product.valor_por_caixa
      || 0;
    const precoPromo = valorUnit > 0 ? +(valorUnit * (1 - pct / 100)).toFixed(2) : 0;
    try {
      await axios.put(`${API}/products/${product.id}/promocao`, {
        promocao_ativa: !!ativar,
        desconto_promocional: pct,
        preco_promocional: precoPromo,
        badge: ativar ? `${pct}% OFF` : '',
      });
      toast.success(ativar ? `Promoção ativada: ${pct}% OFF` : 'Promoção removida');
      setPromoProduct(null);
      await fetchProducts();
    } catch (e) {
      toast.error('Erro ao salvar promoção');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Tem certeza que deseja deletar este produto?');
    if (confirmed) {
      try {
        await axios.delete(`${API}/products/${id}`);
        toast.success('Produto deletado com sucesso!');
        await fetchProducts();
      } catch (error) {
        toast.error('Erro ao deletar produto');
        console.error('Delete error:', error);
      }
    }
  };

  const handleValor1000Change = (valor) => {
    const valor1000 = parseFloat(valor) || 0;
    setFormData({
      ...formData,
      valor_por_1000: valor1000,
      valor_por_100: valor1000 > 0 ? valor1000 / 10 : 0,
      valor_por_unidade: valor1000 > 0 ? valor1000 / 1000 : 0
    });
  };

  // Funções de seleção múltipla
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      setSelectedIds(products.map(p => p.id));
      setSelectAll(true);
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(i => i !== id));
      setSelectAll(false);
    } else {
      setSelectedIds(prev => [...prev, id]);
      if (selectedIds.length + 1 === products.length) {
        setSelectAll(true);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    if (!window.confirm(`Excluir ${selectedIds.length} produto(s)?`)) return;

    try {
      for (const id of selectedIds) {
        await axios.delete(`${API}/products/${id}`);
      }
      toast.success(`${selectedIds.length} produto(s) excluído(s)!`);
      setSelectedIds([]);
      setSelectAll(false);
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao excluir produtos');
    }
  };

  const handleExportCSV = () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }

    const selected = products.filter(p => selectedIds.includes(p.id));
    const headers = ['Código', 'Produto', 'Valor Unidade', 'Valor 100', 'Valor 1000', 'Valor Caixa', 'Estoque'];
    const rows = selected.map(p => [
      p.codigo,
      p.produto,
      (p.valor_por_unidade || 0).toFixed(2),
      (p.valor_por_100 || 0).toFixed(2),
      (p.valor_por_1000 || p.valor_mil || 0).toFixed(2),
      (p.valor_por_caixa || p.valor_caixa || 0).toFixed(2),
      p.quantidade_estoque || p.quantidade_disponivel || 0
    ]);

    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'produtos_exportados.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Arquivo exportado!');
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-heading font-bold text-foreground">Produtos</h2>
          <p className="text-muted-foreground mt-1">{products.length} produtos cadastrados</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              const ok = window.confirm('Vincular cores às fotos de TODOS os produtos antigos?\n\nIsso analisa cada foto e linka com as cores listadas. Produtos que já têm vínculo são pulados. Leva alguns segundos por produto.');
              if (!ok) return;
              const t = toast.loading('Processando todos os produtos...');
              try {
                const resp = await axios.post(`${API}/products/auto-link-colors-all`);
                const { processed = 0, skipped = 0, total_linked = 0 } = resp.data || {};
                toast.dismiss(t);
                toast.success(`✅ ${processed} produto(s) processado(s) • ${total_linked} cor(es) vinculada(s) • ${skipped} já tinham vínculo`);
                fetchProducts();
              } catch (err) {
                toast.dismiss(t);
                toast.error(err.response?.data?.detail || 'Erro ao vincular cores em lote');
              }
            }}
            data-testid="batch-auto-link-colors-btn"
            className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300 text-sm font-medium transition"
            title="Roda detecção de cor em todos os produtos sem vínculo"
          >
            🎨 Vincular cores ↔ fotos (todos)
          </button>
          <button onClick={() => setShowAIModal(true)} data-testid="add-product-ai-btn" className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-lg font-medium transition">
            <Sparkles className="w-5 h-5" />
            Cadastrar com Foto + IA
          </button>
          <button onClick={() => setShowImportModal(true)} data-testid="import-estoque-btn" className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-4 py-2 rounded-lg font-medium transition">
            <FileText className="w-5 h-5" />
            Importar Estoque (Nota/Lista)
          </button>
          <button onClick={openNewModal} data-testid="add-product-btn" className="btn-primary flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Novo Produto
          </button>
        </div>
      </div>

      {/* Importar Estoque IA */}
      <ImportEstoqueModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => { setShowImportModal(false); fetchProducts(); }}
      />

      {/* Barra de Ações em Massa */}
      {selectedIds.length > 0 && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-primary">{selectedIds.length} produto(s) selecionado(s)</span>
            <button onClick={clearSelection} className="text-sm text-muted-foreground hover:text-foreground">Limpar</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExportCSV} className="btn-secondary flex items-center gap-2 text-sm py-2">
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
            <button onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              <Trash2 className="w-4 h-4" />
              Excluir
            </button>
          </div>
        </div>
      )}

      {/* Filtros de Busca — Animated */}
      <AnimatedSearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Buscar por nome ou código..."
        activeFiltersCount={filterCategory ? 1 : 0}
        onClearAll={() => { setSearchTerm(''); setFilterCategory(''); }}
        testIdPrefix="products"
        filtersPanel={
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">Categoria</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="input-field text-sm"
              data-testid="product-category-filter"
            >
              <option value="">Todas Categorias</option>
              {categories.map(([cat, count]) => <option key={cat} value={cat}>{cat} ({count})</option>)}
            </select>
          </div>
        }
        rightSlot={
          <>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1" data-testid="view-mode-toggle">
              <button
                onClick={() => setViewMode('linha')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'linha' ? 'bg-background shadow text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                title="Linha (lista)"
                data-testid="view-mode-linha"
              >
                <Rows className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grade')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'grade' ? 'bg-background shadow text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                title="Grade compacta"
                data-testid="view-mode-grade"
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'card' ? 'bg-background shadow text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                title="Cards grandes"
                data-testid="view-mode-card"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
            <span className="text-sm text-muted-foreground">{filteredProducts.length} de {products.length}</span>
          </>
        }
      />

      {/* Visualização: Linha / Grade / Card */}
      {viewMode === 'linha' && (
      <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="products-list-linha">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-muted">
              <tr>
                <th className="px-2 py-2 w-9"></th>
                <th className="px-1 py-2 w-[56px]"></th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Produto</th>
                <th className="px-2 py-2 text-right text-xs font-semibold w-32">Preço (Caixa/Pacote)</th>
                <th className="px-2 py-2 text-center text-xs font-semibold w-14">Estq</th>
                <th className="px-2 py-2 text-right text-xs font-semibold w-16">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredProducts.map((product) => {
                const estoque = product.quantidade_estoque || product.quantidade_disponivel || 0;
                const alerta = product.alerta_estoque || 50;
                const thumb = product.fotos?.[0] || null;
                
                return (
                  <tr key={product.id} className={`hover:bg-accent/50 transition-colors ${selectedIds.includes(product.id) ? 'bg-primary/5' : ''}`}>
                    <td className="px-2 py-1">
                      <button onClick={() => toggleSelectOne(product.id)} className="p-0.5 hover:bg-accent rounded">
                        {selectedIds.includes(product.id) ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                      </button>
                    </td>
                    <td className="px-1 py-1">
                      <div className="w-14 h-14 rounded-lg bg-muted overflow-hidden flex-shrink-0 border border-border">
                        {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> :
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-base font-bold">{(product.produto || 'P')[0]}</div>}
                      </div>
                    </td>
                    <td className="px-2 py-1 overflow-hidden">
                      <p className="text-sm font-semibold leading-tight truncate">{product.produto}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{product.codigo} {product.categoria ? `• ${product.categoria}` : ''}</p>
                    </td>
                    <td className="px-2 py-1 text-sm text-right tabular-nums">
                      {product.valor_caixa > 0 ? (
                        <>
                          <div className="font-bold text-primary">R$ {(product.valor_caixa || 0).toFixed(2)}</div>
                          <div className="text-[10px] text-muted-foreground leading-none">caixa</div>
                        </>
                      ) : (product.valor_por_unidade || product.valor_unitario) ? (
                        <>
                          <div className="font-bold text-primary">R$ {((product.valor_por_unidade || product.valor_unitario) || 0).toFixed(2)}</div>
                          <div className="text-[10px] text-muted-foreground leading-none">unidade</div>
                        </>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-sm text-center">
                      <span className={estoque <= alerta ? 'text-red-600 font-semibold' : ''}>{estoque}</span>
                    </td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">
                      <button
                        onClick={() => openPromoModal(product)}
                        className={`p-1 mr-0.5 ${product.promocao_ativa ? 'text-orange-600 hover:text-orange-700' : 'text-muted-foreground hover:text-orange-500'}`}
                        title={product.promocao_ativa ? `Em promoção: ${product.desconto_promocional || 0}% OFF` : 'Definir promoção'}
                        data-testid={`promo-btn-${product.id}`}
                      >
                        <Flame className={`w-4 h-4 ${product.promocao_ativa ? 'fill-orange-500/30' : ''}`} />
                      </button>
                      <button onClick={() => openEditModal(product)} className="text-primary hover:text-primary/80 p-1" title="Editar"><Edit className="w-4 h-4" /></button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleDelete(product.id); }} className="text-red-600 hover:text-red-800 p-1" title="Deletar"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-muted-foreground">Nenhum produto encontrado</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Visualização: GRADE COMPACTA */}
      {viewMode === 'grade' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="products-list-grade">
          {filteredProducts.map(product => {
            const unit = getProductSalesUnit(product);
            const estoque = product.quantidade_estoque || product.quantidade_disponivel || 0;
            const alerta = product.alerta_estoque || 50;
            const thumb = product.fotos?.[0] || null;
            const reviews = product.reviews || [];
            const avgRating = reviews.length ? reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length : 0;
            return (
              <div key={product.id} className={`group bg-card border rounded-lg overflow-hidden hover:shadow-md hover:border-primary transition-all ${selectedIds.includes(product.id) ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`}>
                <div className="aspect-square bg-muted relative">
                  {thumb ? (
                    <img src={thumb} alt={product.produto} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-muted-foreground">{(product.produto || 'P')[0]}</div>
                  )}
                  <button
                    onClick={() => toggleSelectOne(product.id)}
                    className="absolute top-1.5 left-1.5 bg-background/90 backdrop-blur p-1 rounded shadow"
                  >
                    {selectedIds.includes(product.id) ? <CheckSquare className="w-3.5 h-3.5 text-primary" /> : <Square className="w-3.5 h-3.5 text-muted-foreground" />}
                  </button>
                  {estoque <= alerta && (
                    <span className="absolute top-1.5 right-1.5 text-[9px] bg-red-500 text-white px-1.5 py-0.5 rounded font-semibold">Baixo</span>
                  )}
                  {product.promocao_ativa && (
                    <span className="absolute bottom-1.5 left-1.5 text-[9px] bg-orange-500 text-white px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
                      <Flame className="w-2.5 h-2.5" /> {product.desconto_promocional || 0}% OFF
                    </span>
                  )}
                </div>
                <div className="p-2 space-y-0.5">
                  <p className="text-xs font-semibold leading-tight line-clamp-2 min-h-[2em]">{product.produto}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{product.codigo}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm font-bold text-primary">R$ {unit.preco.toFixed(2)}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">{unit.label} • Estq {estoque}</p>
                  <div className="flex items-center justify-between pt-1">
                    {reviews.length > 0 ? (
                      <span className="text-[10px] flex items-center gap-0.5 text-amber-500">
                        <Star className="w-2.5 h-2.5 fill-current" /> {avgRating.toFixed(1)} <span className="text-muted-foreground">({reviews.length})</span>
                      </span>
                    ) : <span />}
                    <div className="flex gap-0.5">
                      <button onClick={() => openEditModal(product)} className="p-1 hover:bg-accent rounded" title="Editar"><Edit className="w-3 h-3 text-primary" /></button>
                      <button onClick={() => handleDelete(product.id)} className="p-1 hover:bg-accent rounded" title="Deletar"><Trash2 className="w-3 h-3 text-red-600" /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filteredProducts.length === 0 && (
            <div className="col-span-full text-center text-muted-foreground py-12">Nenhum produto encontrado</div>
          )}
        </div>
      )}

      {/* Visualização: CARDS GRANDES */}
      {viewMode === 'card' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-list-card">
          {filteredProducts.map(product => {
            const unit = getProductSalesUnit(product);
            const estoque = product.quantidade_estoque || product.quantidade_disponivel || 0;
            const alerta = product.alerta_estoque || 50;
            const thumb = product.fotos?.[0] || null;
            const reviews = product.reviews || [];
            const avgRating = reviews.length ? reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length : 0;
            return (
              <div key={product.id} className={`bg-card border rounded-xl overflow-hidden hover:shadow-lg hover:border-primary transition-all ${selectedIds.includes(product.id) ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`}>
                <div className="aspect-[4/3] bg-muted relative">
                  {thumb ? (
                    <img src={thumb} alt={product.produto} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl font-bold text-muted-foreground">{(product.produto || 'P')[0]}</div>
                  )}
                  <button
                    onClick={() => toggleSelectOne(product.id)}
                    className="absolute top-3 left-3 bg-background/90 backdrop-blur p-1.5 rounded-lg shadow"
                  >
                    {selectedIds.includes(product.id) ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                  </button>
                  {product.categoria && (
                    <span className="absolute top-3 right-3 text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full font-medium shadow">{product.categoria}</span>
                  )}
                  {estoque <= alerta && (
                    <span className="absolute bottom-3 right-3 text-xs bg-red-500 text-white px-2 py-1 rounded-full font-semibold shadow">Estoque Baixo</span>
                  )}
                </div>
                <div className="p-4 space-y-2">
                  <div>
                    <h3 className="text-base font-bold leading-tight">{product.produto}</h3>
                    <p className="text-xs text-muted-foreground">Cód: <strong>{product.codigo}</strong></p>
                  </div>
                  {/* Estrelas */}
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Star key={n} className={`w-4 h-4 ${n <= Math.round(avgRating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">
                      {reviews.length > 0 ? `${avgRating.toFixed(1)} (${reviews.length} ${reviews.length === 1 ? 'avaliação' : 'avaliações'})` : 'Sem avaliações'}
                    </span>
                  </div>
                  <div className="flex items-end justify-between pt-1">
                    <div>
                      <p className="text-2xl font-bold text-primary tabular-nums">R$ {unit.preco.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{unit.label} • Estoque: <strong className={estoque <= alerta ? 'text-red-600' : ''}>{estoque}</strong></p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEditModal(product)} className="p-2 hover:bg-accent rounded-lg" title="Editar"><Edit className="w-4 h-4 text-primary" /></button>
                      <button onClick={() => handleDelete(product.id)} className="p-2 hover:bg-accent rounded-lg" title="Deletar"><Trash2 className="w-4 h-4 text-red-600" /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filteredProducts.length === 0 && (
            <div className="col-span-full text-center text-muted-foreground py-12">Nenhum produto encontrado</div>
          )}
        </div>
      )}

      {/* Modal de Edição/Criação */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden m-4">
            {/* Header do Modal */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-xl font-heading font-semibold flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </h3>
              <button onClick={closeModal} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border">
              <button
                type="button"
                onClick={() => setActiveTab('dados')}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'dados' 
                    ? 'text-primary border-b-2 border-primary bg-primary/5' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Dados do Produto
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('catalogo')}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'catalogo' 
                    ? 'text-primary border-b-2 border-primary bg-primary/5' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Catálogo (Fotos/Vídeos)
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('variacoes')}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'variacoes'
                    ? 'text-primary border-b-2 border-primary bg-primary/5'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-variacoes"
              >
                Variações {formData.variacoes?.length > 0 && (
                  <span className="ml-1 text-[10px] font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                    {formData.variacoes.length}
                  </span>
                )}
              </button>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Tab Dados do Produto */}
              {activeTab === 'dados' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Código *</label>
                    <input type="text" value={formData.codigo} onChange={(e) => setFormData({...formData, codigo: e.target.value})} className="input-field" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome do Produto *</label>
                    <input type="text" value={formData.produto} onChange={(e) => setFormData({...formData, produto: e.target.value})} className="input-field" required />
                  </div>
                  
                  {/* Seção de Preços - Bloco Seletor */}
                  <div className="md:col-span-2 border-t border-border pt-4 mt-2">
                    <h4 className="text-sm font-semibold mb-1 text-muted-foreground">Preços por Tipo de Venda</h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Clique nos tipos que esse produto é vendido. O campo de valor aparece logo abaixo.
                    </p>
                    {(() => {
                      const tipos = [
                        { key: 'valor_por_unidade', label: 'Unidade', icon: '1' },
                        { key: 'valor_por_caixa',   label: 'Caixa',   icon: 'CX' },
                        { key: 'valor_pacote',      label: 'Pacote',  icon: 'PC' },
                        { key: 'valor_por_100',     label: 'Cento',   icon: '100' },
                        { key: 'valor_por_1000',    label: 'Milheiro',icon: '1k' },
                        { key: 'valor_por_kg',      label: 'KG',      icon: 'KG' },
                      ];
                      const toggleTipo = (key) => {
                        const ativo = parseFloat(formData[key]) > 0 || formData[`__active_${key}`];
                        if (ativo) {
                          // desativa: zera valor e remove marca
                          setFormData({ ...formData, [key]: 0, [`__active_${key}`]: false });
                        } else {
                          // ativa: mantém valor atual (ou 0) e marca como ativo
                          setFormData({ ...formData, [`__active_${key}`]: true });
                        }
                      };
                      return (
                        <div className="space-y-3">
                          {/* Chips dos tipos */}
                          <div className="flex flex-wrap gap-2">
                            {tipos.map(t => {
                              const ativo = parseFloat(formData[t.key]) > 0 || formData[`__active_${t.key}`];
                              return (
                                <button
                                  type="button"
                                  key={t.key}
                                  onClick={() => toggleTipo(t.key)}
                                  data-testid={`tipo-toggle-${t.label.toLowerCase()}`}
                                  className={`
                                    flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium
                                    transition-all border-2
                                    ${ativo
                                      ? 'bg-primary text-primary-foreground border-primary shadow-md scale-105'
                                      : 'bg-muted text-foreground border-border hover:border-primary/50 hover:bg-accent'}
                                  `}
                                >
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ativo ? 'bg-white/20' : 'bg-primary/10 text-primary'}`}>
                                    {t.icon}
                                  </span>
                                  {t.label}
                                  {ativo && <span className="text-xs">✓</span>}
                                </button>
                              );
                            })}
                          </div>

                          {/* Campos de valor — só aparecem para os tipos ativos */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                            {tipos.map(t => {
                              const ativo = parseFloat(formData[t.key]) > 0 || formData[`__active_${t.key}`];
                              if (!ativo) return null;
                              return (
                                <div key={t.key} className="bg-accent/30 border border-border rounded-lg p-3 animate-in fade-in slide-in-from-top-1">
                                  <label className="block text-sm font-medium mb-1">
                                    Valor por <span className="text-primary font-bold">{t.label}</span>
                                  </label>
                                  <CurrencyInput
                                    value={formData[t.key]}
                                    onChange={(val) => setFormData({...formData, [t.key]: val})}
                                    placeholder="0,00"
                                    className="input-field"
                                    data-testid={`product-${t.key}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Seção de Estoque */}
                  <div className="md:col-span-2 border-t border-border pt-4 mt-2">
                    <h4 className="text-sm font-semibold mb-3 text-muted-foreground">Estoque</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Quantidade em Estoque *</label>
                        <input type="number" value={formData.quantidade_estoque} onChange={(e) => setFormData({...formData, quantidade_estoque: e.target.value})} className="input-field" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Alerta de Estoque Baixo</label>
                        <input type="number" value={formData.alerta_estoque} onChange={(e) => setFormData({...formData, alerta_estoque: e.target.value})} className="input-field" placeholder="50" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab Catálogo */}
              {activeTab === 'catalogo' && (
                <div className="space-y-6">
                  {/* Categoria e Tamanho */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center justify-between text-sm font-medium mb-2">
                        <span>Categoria / Coleção</span>
                        {formData.categoria && !siteCategories.some(c => (c.name || '').toLowerCase() === formData.categoria.toLowerCase()) && (
                          <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">
                            Nova — será criada na Coleção
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        list="produto-categorias-existentes"
                        value={formData.categoria}
                        onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                        className="input-field"
                        placeholder="Comece a digitar ou escolha..."
                        autoComplete="off"
                        data-testid="produto-categoria-input"
                      />
                      <datalist id="produto-categorias-existentes">
                        {siteCategories.map(c => (
                          <option key={c.id || c.name} value={c.name} />
                        ))}
                      </datalist>
                      {siteCategories.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {siteCategories.slice(0, 6).map(c => (
                            <button
                              key={c.id || c.name}
                              type="button"
                              onClick={() => setFormData({...formData, categoria: c.name})}
                              className={`text-[11px] px-2 py-0.5 rounded-full border transition ${formData.categoria === c.name ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-accent'}`}
                              data-testid={`chip-cat-${c.name}`}
                            >
                              {c.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Tamanho</label>
                      <input 
                        type="text" 
                        value={formData.tamanho} 
                        onChange={(e) => setFormData({...formData, tamanho: e.target.value})} 
                        className="input-field" 
                        placeholder="Ex: Pequeno, Médio, Grande, 10x15cm..."
                      />
                    </div>
                  </div>

                  {/* Descrição */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Descrição</label>
                    <textarea 
                      value={formData.descricao} 
                      onChange={(e) => setFormData({...formData, descricao: e.target.value})} 
                      className="input-field h-24 resize-none" 
                      placeholder="Descrição curta do produto para o catálogo..."
                    />
                  </div>

                  {/* Detalhes */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Detalhes Técnicos</label>
                    <textarea 
                      value={formData.detalhes} 
                      onChange={(e) => setFormData({...formData, detalhes: e.target.value})} 
                      className="input-field h-24 resize-none" 
                      placeholder="Especificações técnicas, materiais, dimensões..."
                    />
                  </div>

                  {/* Cores */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Cores Disponíveis</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(formData.cores || []).map((cor, i) => (
                        <span key={i} className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs">
                          <span
                            className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600"
                            style={{ background: getColorHex(cor) }}
                            title={cor}
                          />
                          {cor}
                          <button type="button" onClick={() => {
                            const newCores = [...(formData.cores || [])];
                            newCores.splice(i, 1);
                            setFormData({...formData, cores: newCores});
                          }} className="text-red-500 hover:text-red-700 ml-1">×</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Adicionar cor (ex: Vermelho, Azul marinho, Rosa bebê)"
                        className="input-field flex-1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            e.preventDefault();
                            setFormData({...formData, cores: [...(formData.cores || []), e.target.value.trim()]});
                            e.target.value = '';
                          }
                        }} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Enter para adicionar. Aceita: vermelho, azul marinho, rosa bebê, holográfico, prata, dourado, bege, coral, salmão, etc.</p>
                  </div>

                  {/* Especificações */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Especificações</label>
                    <textarea 
                      value={formData.especificacoes} 
                      onChange={(e) => setFormData({...formData, especificacoes: e.target.value})} 
                      className="input-field h-20 resize-none" 
                      placeholder="Especificações detalhadas do produto..."
                    />
                  </div>

                  {/* Fotos - UPLOAD (até 30) com detecção AUTOMÁTICA de cor */}
                  <div>
                    {editingProduct && (formData.fotos?.length || 0) > 0 && (
                      <div className="mb-2 flex flex-wrap items-center gap-2 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                        <span className="text-xs text-purple-900 dark:text-purple-100 flex-1 min-w-[200px]">
                          🎨 <b>Vincular cores às fotos existentes:</b> detecta a cor de cada foto e linka com as cores listadas.
                        </span>
                        <button
                          type="button"
                          onClick={autoLinkCoresExistentes}
                          disabled={detectandoCores}
                          className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold disabled:opacity-50 flex items-center gap-1.5"
                          data-testid="auto-link-cores-btn"
                        >
                          {detectandoCores ? (
                            <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processando...</>
                          ) : (
                            <>🔗 Vincular agora</>
                          )}
                        </button>
                      </div>
                    )}
                    <MultiImageUploader
                      values={formData.fotos || []}
                      onChange={(urls) => {
                        // Remove cores cujas fotos não estão mais presentes
                        const novoMapa = { ...(formData.cores_foto_map || {}) };
                        Object.keys(novoMapa).forEach(cor => {
                          if (!urls.includes(novoMapa[cor])) delete novoMapa[cor];
                        });
                        const novasCores = (formData.cores || []).filter(c => novoMapa[c] || !formData.cores_foto_map?.[c]);
                        setFormData({ ...formData, fotos: urls, cores_foto_map: novoMapa, cores: novasCores });
                      }}
                      onUploaded={(novasUrls) => detectarCoresPorFoto(novasUrls)}
                      folder="products"
                      max={30}
                      label="Fotos do Produto (detecção automática de cor)"
                    />
                    {detectandoCores && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 px-3 py-1.5 rounded-lg" data-testid="detectando-cores-indicator">
                        <div className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                        Detectando cores das fotos enviadas...
                      </div>
                    )}
                    {/* Preview das cores detectadas + link com fotos */}
                    {Object.keys(formData.cores_foto_map || {}).length > 0 && (
                      <div className="mt-3 p-3 bg-muted/40 border border-border rounded-lg" data-testid="cores-detectadas-preview">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase">Cores detectadas automaticamente ({Object.keys(formData.cores_foto_map).length})</p>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, cores_foto_map: {}, cores: [] })}
                            className="text-[10px] text-red-600 hover:underline"
                            data-testid="limpar-cores-detectadas"
                          >
                            Limpar tudo
                          </button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {Object.entries(formData.cores_foto_map).map(([cor, foto]) => (
                            <div key={cor} className="flex items-center gap-2 bg-card border border-border rounded-lg p-2" data-testid={`cor-detectada-${cor}`}>
                              <img src={foto.startsWith('http') ? foto : `${process.env.REACT_APP_BACKEND_URL}${foto}`} alt={cor} className="w-10 h-10 object-cover rounded" />
                              <div className="flex-1 min-w-0">
                                <input
                                  type="text"
                                  value={cor}
                                  onChange={(e) => {
                                    const novoNome = e.target.value.trim();
                                    if (!novoNome || novoNome === cor) return;
                                    const novoMapa = { ...formData.cores_foto_map };
                                    delete novoMapa[cor];
                                    novoMapa[novoNome] = foto;
                                    const novasCores = (formData.cores || []).map(c => c === cor ? novoNome : c);
                                    setFormData({ ...formData, cores_foto_map: novoMapa, cores: novasCores });
                                  }}
                                  className="w-full text-xs font-medium bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-1"
                                />
                                <p className="text-[9px] text-muted-foreground truncate">→ foto vinculada</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const novoMapa = { ...formData.cores_foto_map };
                                  delete novoMapa[cor];
                                  const novasCores = (formData.cores || []).filter(c => c !== cor);
                                  setFormData({ ...formData, cores_foto_map: novoMapa, cores: novasCores });
                                }}
                                className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-0.5 rounded"
                                title="Remover esta cor"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-2">
                          💡 Quando o cliente clicar nessa cor no catálogo, verá automaticamente a foto vinculada.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Fotos de Especificações */}
                  <MultiImageUploader
                    values={formData.fotos_especificacoes || []}
                    onChange={(urls) => setFormData({...formData, fotos_especificacoes: urls})}
                    folder="products"
                    max={5}
                    label="Fotos de Especificações"
                  />

                  {/* Vídeos - UPLOAD */}
                  <VideoUploader
                    values={formData.videos || []}
                    onChange={(urls) => setFormData({...formData, videos: urls})}
                    folder="products"
                    max={3}
                    label="Vídeos do Produto"
                  />

                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <p className="text-sm text-green-800 dark:text-green-200">
                      <strong>Dica:</strong> Clique para fazer upload diretamente do seu computador. 
                      A primeira foto será a principal no catálogo.
                    </p>
                  </div>
                </div>
              )}

              {/* Tab Variações */}
              {activeTab === 'variacoes' && (
                <VariacoesEditor
                  variacoes={formData.variacoes || []}
                  onChange={(v) => setFormData(prev => ({ ...prev, variacoes: v }))}
                />
              )}

              {/* Botões */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingProduct ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal AI Quick Register */}
      {showAIModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="ai-product-modal">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                <div>
                  <h3 className="font-semibold">Cadastrar Produto com IA</h3>
                  <p className="text-xs opacity-90">Tire ou envie uma foto + dados básicos → IA preenche nome, categoria, descrição e cores</p>
                </div>
              </div>
              <button onClick={() => !aiLoading && setShowAIModal(false)} className="hover:bg-white/20 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-4">
              {!aiSuggestions ? (
                <>
                  {/* Upload area */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Foto do Produto *</label>
                    <div className="border-2 border-dashed border-border rounded-lg p-4 flex items-center justify-center">
                      {aiPhotoPreview ? (
                        <div className="relative">
                          <img src={aiPhotoPreview} alt="preview" className="max-h-64 rounded-lg" />
                          <button
                            onClick={() => { setAiPhoto(null); setAiPhotoPreview(null); }}
                            className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer flex flex-col items-center gap-2 py-6 text-muted-foreground hover:text-primary transition">
                          <Camera className="w-12 h-12" />
                          <span className="text-sm">Clique para enviar ou arraste uma foto</span>
                          <span className="text-xs">JPG, PNG ou WebP (max 5MB)</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) {
                                setAiPhoto(f);
                                setAiPhotoPreview(URL.createObjectURL(f));
                              }
                            }}
                            data-testid="ai-photo-input"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2">
                    💡 Opcional: preencha dicas abaixo para a IA gerar um cadastro mais preciso. Se não preencher, ela deduz tudo pela foto.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Nome sugerido (opcional)</label>
                      <input type="text" value={aiHintNome} onChange={(e) => setAiHintNome(e.target.value)}
                        placeholder="ex: Laço chanel veludo" className="input-field"
                        data-testid="ai-hint-nome" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Categoria (opcional)</label>
                      <input type="text" value={aiHintCategoria} onChange={(e) => setAiHintCategoria(e.target.value)}
                        placeholder="ex: Laços, Sacolas, Caixas..." className="input-field"
                        data-testid="ai-hint-categoria" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Notas/descrição (opcional)</label>
                    <textarea value={aiHintDescricao} onChange={(e) => setAiHintDescricao(e.target.value)}
                      rows={2}
                      placeholder="ex: material veludo, tamanho médio..."
                      className="input-field"
                      data-testid="ai-hint-descricao" />
                  </div>
                </>
              ) : (
                <>
                  {/* Preview + sugestões editáveis */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                      <img src={aiPhotoPreview} alt="" className="w-full rounded-lg border border-border" />
                    </div>
                    <div className="md:col-span-2 space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Nome do Produto</label>
                        <input type="text"
                          value={aiSuggestions.produto_sugerido || ''}
                          onChange={(e) => setAiSuggestions({...aiSuggestions, produto_sugerido: e.target.value})}
                          className="input-field"
                          data-testid="ai-suggestion-nome" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium mb-1">Categoria</label>
                          <input type="text"
                            value={aiSuggestions.categoria_sugerida || ''}
                            onChange={(e) => setAiSuggestions({...aiSuggestions, categoria_sugerida: e.target.value})}
                            className="input-field" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Preço Caixa (R$) *</label>
                          <input type="text" inputMode="decimal"
                            value={aiPreco}
                            onChange={(e) => setAiPreco(e.target.value.replace(/[^0-9.,]/g, ''))}
                            placeholder="0,00"
                            className="input-field"
                            data-testid="ai-preco-input" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Descrição</label>
                        <textarea
                          value={aiSuggestions.descricao || ''}
                          onChange={(e) => setAiSuggestions({...aiSuggestions, descricao: e.target.value})}
                          rows={2}
                          className="input-field" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Especificações</label>
                        <textarea
                          value={aiSuggestions.especificacoes || ''}
                          onChange={(e) => setAiSuggestions({...aiSuggestions, especificacoes: e.target.value})}
                          rows={2}
                          className="input-field" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Cores detectadas</label>
                        <div className="flex flex-wrap gap-2">
                          {(Array.isArray(aiSuggestions.cores_detectadas) ? aiSuggestions.cores_detectadas : []).map((cor, i) => (
                            <span key={i} className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs">
                              <span className="w-4 h-4 rounded-full border border-gray-400" style={{ background: getColorHex(cor) }} />
                              {cor}
                              <button onClick={() => {
                                const novo = [...(Array.isArray(aiSuggestions.cores_detectadas) ? aiSuggestions.cores_detectadas : [])];
                                novo.splice(i, 1);
                                setAiSuggestions({...aiSuggestions, cores_detectadas: novo});
                              }} className="text-red-500 ml-1">×</button>
                            </span>
                          ))}
                        </div>
                        <input type="text" placeholder="Adicionar cor + Enter"
                          className="input-field mt-2"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              e.preventDefault();
                              const atuais = Array.isArray(aiSuggestions.cores_detectadas) ? aiSuggestions.cores_detectadas : [];
                              setAiSuggestions({...aiSuggestions, cores_detectadas: [...atuais, e.target.value.trim()]});
                              e.target.value = '';
                            }
                          }} />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="px-5 py-3 border-t border-border bg-muted flex items-center justify-between gap-2">
              <button onClick={() => !aiLoading && setShowAIModal(false)} className="btn-secondary" disabled={aiLoading}>
                Cancelar
              </button>
              {!aiSuggestions ? (
                <button
                  onClick={handleAIAnalyze}
                  disabled={aiLoading || !aiPhoto}
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-5 py-2 rounded-lg font-medium disabled:opacity-50"
                  data-testid="ai-analyze-btn"
                >
                  {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analisando com IA...</> : <><Sparkles className="w-4 h-4" /> Analisar com IA</>}
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setAiSuggestions(null); setAiImageUrl(null); }}
                    className="btn-secondary"
                  >
                    ← Voltar
                  </button>
                  <button
                    onClick={handleAICreateProduct}
                    className="btn-primary flex items-center gap-2"
                    data-testid="ai-save-btn"
                  >
                    <Plus className="w-4 h-4" /> Cadastrar Produto
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Promoção */}
      {promoProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPromoProduct(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3" data-testid="promo-modal">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              <h3 className="font-bold text-lg">Promoção do produto</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              <b>{promoProduct.product.produto}</b>
              <span className="block text-xs opacity-75">{promoProduct.product.codigo}</span>
            </p>
            <label className="flex items-center gap-2 text-sm bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 px-3 py-2 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={promoProduct.ativar}
                onChange={(e) => setPromoProduct({ ...promoProduct, ativar: e.target.checked })}
                data-testid="promo-toggle"
              />
              <span><b>Ativar promoção</b> — produto aparece com badge na área do cliente</span>
            </label>
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">% de desconto</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="99"
                  step="1"
                  value={promoProduct.desconto_pct}
                  onChange={(e) => setPromoProduct({ ...promoProduct, desconto_pct: e.target.value })}
                  disabled={!promoProduct.ativar}
                  className="input-field text-sm w-24 text-center disabled:opacity-50"
                  data-testid="promo-desconto-input"
                />
                <span className="text-sm">% OFF</span>
                {promoProduct.ativar && promoProduct.desconto_pct > 0 && (
                  <span className="ml-auto text-xs text-orange-600 dark:text-orange-400">
                    {(() => {
                      const p = promoProduct.product;
                      const vu = p.valor_por_unidade
                        || (p.quantidade_caixa > 0 ? (p.valor_caixa || 0) / p.quantidade_caixa : 0)
                        || p.valor_por_caixa
                        || 0;
                      const novo = vu * (1 - (parseFloat(promoProduct.desconto_pct) || 0) / 100);
                      return vu > 0 ? `R$ ${vu.toFixed(2)} → R$ ${novo.toFixed(2)}` : '';
                    })()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button
                onClick={() => setPromoProduct(null)}
                className="px-3 py-1.5 text-sm rounded hover:bg-accent"
                data-testid="promo-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={savePromo}
                className="px-4 py-1.5 text-sm font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded flex items-center gap-1.5"
                data-testid="promo-save-btn"
              >
                <Flame className="w-3.5 h-3.5" /> Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modal de importação de estoque via IA (NFe ou lista manuscrita)
function ImportEstoqueModal({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1=upload, 2=analyzing, 3=review, 4=done
  const [files, setFiles] = useState([]);
  const [analyzed, setAnalyzed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [documento, setDocumento] = useState('');

  React.useEffect(() => {
    if (!open) return;
    setStep(1); setFiles([]); setAnalyzed(null); setDocumento('');
    axios.get(`${API_BASE}/products`).then(r => setAllProducts(r.data || [])).catch(() => {});
  }, [open]);

  const handleFiles = (e) => {
    const fl = Array.from(e.target.files || []).slice(0, 5);
    setFiles(fl);
  };

  const analyze = async () => {
    if (files.length === 0) { toast.error('Selecione 1+ imagens'); return; }
    setStep(2);
    try {
      const dataUrls = await Promise.all(files.map(f => new Promise((res, rej) => {
        const r = new FileReader();
        r.onloadend = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(f);
      })));
      const res = await axios.post(`${API_BASE}/admin/import-estoque/analyze`, { images: dataUrls });
      setAnalyzed(res.data);
      setDocumento(res.data.documento || '');
      setStep(3);
      if ((res.data.items || []).length === 0) {
        toast.warning('A IA não identificou produtos nas imagens. Tente fotos mais nítidas.');
      } else {
        toast.success(`${res.data.items.length} produto(s) identificado(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao analisar imagens');
      setStep(1);
    }
  };

  const updateItem = (idx, patch) => {
    setAnalyzed(prev => ({
      ...prev,
      items: prev.items.map((it, i) => i === idx ? { ...it, ...patch } : it)
    }));
  };

  const removeItem = (idx) => {
    setAnalyzed(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const confirm = async () => {
    if (!analyzed || (analyzed.items || []).length === 0) {
      toast.error('Nenhum item para confirmar'); return;
    }
    const noMatch = analyzed.items.filter(it => !it.matched_product_id);
    if (noMatch.length > 0 && !window.confirm(`${noMatch.length} item(ns) não estão vinculados a produto cadastrado. Eles serão ignorados. Continuar?`)) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/import-estoque/confirm`, {
        items: analyzed.items, documento, observacao: ''
      });
      toast.success(`✅ Estoque atualizado: ${res.data.atualizados.length} produto(s)`, { duration: 5000 });
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao confirmar');
    }
    setSubmitting(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="import-estoque-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <h2 className="font-bold">Importar Estoque (NFe ou Lista) com IA</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg" data-testid="import-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === 1 && (
            <div className="space-y-4" data-testid="import-step-upload">
              <p className="text-sm text-muted-foreground">
                Envie 1 a 5 fotos de <strong>Nota Fiscal Eletrônica</strong> impressa ou <strong>lista manuscrita</strong> (formato &quot;0# 50cx&quot;).
                A IA vai ler, identificar os produtos e mostrar uma tabela para você conferir antes de dar entrada no estoque.
              </p>
              <label className="block border-2 border-dashed border-emerald-300 rounded-xl p-8 text-center cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/10" data-testid="import-file-area">
                <Upload className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
                <p className="font-semibold">Clique para selecionar fotos</p>
                <p className="text-xs text-muted-foreground">JPG/PNG até 5MB cada · máximo 5 imagens</p>
                <input type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={handleFiles} data-testid="import-file-input" />
              </label>
              {files.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="import-files-preview">
                  {files.map((f, i) => <div key={i} className="text-xs p-2 bg-gray-100 dark:bg-gray-900/40 rounded truncate">{f.name}</div>)}
                </div>
              )}
              <button onClick={analyze} disabled={files.length === 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2"
                data-testid="import-analyze-btn">
                <Sparkles className="w-4 h-4" /> Analisar com IA
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="text-center py-12" data-testid="import-step-analyzing">
              <Loader2 className="w-10 h-10 mx-auto text-emerald-600 animate-spin mb-3" />
              <p className="font-semibold">Analisando com Gemini Vision...</p>
              <p className="text-xs text-muted-foreground mt-1">Pode levar 10-30 segundos.</p>
            </div>
          )}

          {step === 3 && analyzed && (
            <div className="space-y-3" data-testid="import-step-review">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-sm font-semibold">Tipo detectado: <span className="text-emerald-700">{analyzed.tipo || 'desconhecido'}</span></p>
                  <p className="text-xs text-muted-foreground">{analyzed.items?.length || 0} item(ns) identificado(s)</p>
                </div>
                <input
                  type="text" value={documento} onChange={(e) => setDocumento(e.target.value)}
                  placeholder="Documento (opcional, ex: NFe 12345)"
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
                  data-testid="import-documento"
                />
              </div>

              <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="w-full text-xs" data-testid="import-items-table">
                  <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">Código IA</th>
                      <th className="px-2 py-2 text-left">Descrição IA</th>
                      <th className="px-2 py-2 text-right">Qtd</th>
                      <th className="px-2 py-2 text-left">Produto cadastrado</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {(analyzed.items || []).map((it, idx) => (
                      <tr key={idx} className={it.matched_product_id ? '' : 'bg-amber-50 dark:bg-amber-900/10'} data-testid={`import-row-${idx}`}>
                        <td className="px-2 py-1.5 font-mono">{it.codigo}</td>
                        <td className="px-2 py-1.5 max-w-xs truncate" title={it.descricao}>{it.descricao}</td>
                        <td className="px-2 py-1.5 text-right">
                          <input type="number" min="0" value={it.quantidade}
                            onChange={(e) => updateItem(idx, { quantidade: parseFloat(e.target.value) || 0 })}
                            className="w-20 px-1.5 py-0.5 text-right border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900"
                            data-testid={`import-qty-${idx}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <select
                            value={it.matched_product_id || ''}
                            onChange={(e) => {
                              const p = allProducts.find(pp => pp.id === e.target.value);
                              updateItem(idx, {
                                matched_product_id: e.target.value || null,
                                matched_codigo: p?.codigo || null,
                                matched_nome: p?.produto || p?.nome || null,
                              });
                            }}
                            className="w-full px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900"
                            data-testid={`import-match-${idx}`}
                          >
                            <option value="">— sem vínculo (ignorar) —</option>
                            {allProducts.map(p => (
                              <option key={p.id} value={p.id}>{p.codigo} · {(p.produto || p.nome || '').slice(0, 40)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <button onClick={() => removeItem(idx)} className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" data-testid={`import-remove-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <AlertTriangle className="w-3 h-3 text-amber-500" /> Linhas em amarelo não têm produto vinculado — selecione um ou serão ignoradas.
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button onClick={() => setStep(1)} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg" data-testid="import-back-btn">
                  Voltar
                </button>
                <button onClick={confirm} disabled={submitting}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-lg flex items-center gap-2"
                  data-testid="import-confirm-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {submitting ? 'Atualizando...' : `Confirmar Entrada no Estoque (${(analyzed.items || []).filter(i => i.matched_product_id).length} itens)`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
